// src/hooks/useCarbonIntensity.js
import { gql } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import { useMemo } from "react";

const EMIS_QUERY = gql`
  query EmisElc($scenario: String!, $from: Int!, $to: Int!) {
    timeSeries(view: EMIS_GHG_BY_SET, scenario: $scenario, fromYear: $from, toYear: $to) {
      year
      group   # e.g. "EMIS GHG ELC"
      value   # ktCO2e
    }
  }
`;

const GEN_QUERY = gql`
  query GenElc($scenario: String!, $from: Int!, $to: Int!) {
    timeSeries(view: ELC_GEN, scenario: $scenario, fromYear: $from, toYear: $to) {
      year
      value   # PJ
    }
  }
`;

const norm = (s) => String(s ?? "").trim().replace(/\s+/g, " ").toUpperCase();
// tolerant matcher for the electricity bucket in EMIS sets
const isEmisElc = (g) => {
  const x = norm(g);
  if (x === "ELC") return true;
  // fallbacks: some data exports vary slightly
  if (/^EMIS\s+GHG\s+ELC\b/.test(x)) return true;
  // safety: treat anything that starts with EMIS GHG and ends with ELC as electricity
  if (x.startsWith("EMIS GHG ") && x.endsWith(" ELC")) return true;
  return false;
};

export function useCarbonIntensity({
  scenario,
  from = 2025,
  to = 2050,
  pjToTwh = 0.277778, // PJ -> TWh
  debug = false,
} = {}) {
  const skip = !scenario || !from || !to;

  const { data: emisData, loading: emisLoading, error: emisError } = useQuery(EMIS_QUERY, {
    variables: { scenario, from, to },
    skip,
    fetchPolicy: "cache-first",
  });

  const { data: genData, loading: genLoading, error: genError } = useQuery(GEN_QUERY, {
    variables: { scenario, from, to },
    skip,
    fetchPolicy: "cache-first",
  });

  const loading = !skip && (emisLoading || genLoading);
  const error = skip ? null : (emisError || genError);

  const {
    labels,
    intensity,
    baselineYear,
    finalYear,
    baselineIntensity,
    finalIntensity,
    intensityReductionPercentage,
    _debug,
  } = useMemo(() => {
    const emisRows = emisData?.timeSeries ?? [];
    const genRows  = genData?.timeSeries ?? [];

    // Sum emissions (kt) for electricity set
    const emisByYear = new Map();
    for (const r of emisRows) {
      if (!isEmisElc(r.group)) continue;
      const y = Number(r.year);
      const v = Number(r.value) || 0; // kt
      emisByYear.set(y, (emisByYear.get(y) || 0) + v);
    }

    // Sum generation (PJ) across all rows, convert to TWh
    const genByYear = new Map();
    for (const r of genRows) {
      const y = Number(r.year);
      const vPJ = Number(r.value) || 0;
      const vTWh = vPJ * pjToTwh;
      genByYear.set(y, (genByYear.get(y) || 0) + vTWh);
    }

    const years = Array.from(new Set([...emisByYear.keys(), ...genByYear.keys()]))
      .filter((y) => y >= from && y <= to)
      .sort((a, b) => a - b);

    const labels = years.map(String);

    const intensity = years.map((y) => {
      const kt = emisByYear.get(y) ?? 0;
      const twh = genByYear.get(y) ?? 0;
      if (!twh || !isFinite(twh)) return 0;
      // kt / TWh = g/kWh (1 kt = 1e9 g; 1 TWh = 1e9 kWh)
      return kt / twh;
    });

    const baselineYear = years[0] ?? null;
    const finalYear = years[years.length - 1] ?? null;
    const baselineIntensity = intensity.length ? intensity[0] : null;
    const finalIntensity = intensity.length ? intensity[intensity.length - 1] : null;

    let intensityReductionPercentage = null;
    if (baselineIntensity && baselineIntensity !== 0 && finalIntensity != null) {
      intensityReductionPercentage = ((baselineIntensity - finalIntensity) / baselineIntensity) * 100;
    }

    const _debug = {
      emisRowCount: emisRows.length,
      genRowCount: genRows.length,
      years,
      emisYearsWithData: [...emisByYear.entries()],
      genYearsWithData: [...genByYear.entries()],
    };

    return {
      labels,
      intensity,
      baselineYear,
      finalYear,
      baselineIntensity,
      finalIntensity,
      intensityReductionPercentage,
      _debug,
    };
  }, [emisData, genData, from, to, pjToTwh]);

  if (debug && !skip) {
    // minimal but helpful visibility in the console
    // eslint-disable-next-line no-console
    console.debug("[useCarbonIntensity]", { scenario, from, to, loading, error, _debug });
  }

  return {
    loading,
    error,
    labels,
    intensity,
    baselineYear,
    finalYear,
    baselineIntensity,
    finalIntensity,
    intensityReductionPercentage,
  };
}
